import { Upload as UploadIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useUploadDocument } from '@src/api/document-api'
import { SUPPORTED_DOCUMENT_TYPES } from '@src/types/constants'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface ImportDialogProps {
  open: boolean
  onClose: () => void
}

const ImportDialog = ({ open, onClose }: ImportDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const uploadMutation = useUploadDocument()

  const addFiles = useCallback((newFiles: File[]) => {
    setSelectedFiles((prev) => {
      const combined = [...prev, ...newFiles]
      return combined.filter(
        (file, index, self) => index === self.findIndex((f) => f.name === file.name && f.size === file.size),
      )
    })
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      if (uploadMutation.isPending) return
      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) addFiles(files)
    },
    [uploadMutation.isPending, addFiles],
  )

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      addFiles(Array.from(files))
    }
    event.target.value = ''
  }

  const handleClose = () => {
    setSelectedFiles([])
    onClose()
  }

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) return
    try {
      const result = await uploadMutation.mutateAsync(selectedFiles)
      enqueueSnackbar(t('common.messages.success'), { variant: 'success' })
      handleClose()
      if (result?.document?.id) {
        sessionStorage.setItem('lastViewedDocId', result.document.id)
        navigate(`/documents/${result.document.id}`)
      }
    } catch (_error) {
      enqueueSnackbar(t('common.messages.error'), { variant: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={uploadMutation.isPending ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('common.importContent')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={SUPPORTED_DOCUMENT_TYPES}
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={uploadMutation.isPending}
          />
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploadMutation.isPending && fileInputRef.current?.click()}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: isDragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: uploadMutation.isPending ? 'default' : 'pointer',
              bgcolor: isDragging ? 'action.hover' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': uploadMutation.isPending ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('common.selectFile')}
            </Typography>
          </Box>

          {uploadMutation.isPending && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress />
              <Typography variant="body1" color="primary">
                {t('common.savePending')}
              </Typography>
            </Box>
          )}

          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {selectedFiles.length} {selectedFiles.length === 1 ? t('common.file') : t('common.files')}
              </Typography>
              <Stack spacing={1}>
                {selectedFiles.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file.name}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                      disabled={uploadMutation.isPending}
                    >
                      {t('common.delete')}
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadMutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedFiles.length === 0 || uploadMutation.isPending}
        >
          {uploadMutation.isPending ? t('common.load') : t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportDialog
